import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LandingNavbar, LandingFooter } from '@/components/landing';
import { CalButton } from '@/components/CalButton';
import '@/styles/landing.css';
import { 
  ArrowRight, 
  Zap, 
  MapPin, 
  Search, 
  TrendingUp, 
  CheckCircle2,
  AlertTriangle,
  ArrowDownRight,
  Activity,
  Sparkles,
  ArrowUpRight,
  AlertCircle,
  Clock,
  Database,
  ShieldAlert,
  BarChart3,
  RefreshCw,
  Cpu,
  Network,
  Rocket
} from 'lucide-react';

// --- Animation Hook ---
const useIntersectionObserver = (options: IntersectionObserverInit = {}): [React.RefObject<HTMLDivElement>, boolean] => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.1, ...options });

    if (elementRef.current) observer.observe(elementRef.current);
    return () => observer.disconnect();
  }, [options]);

  return [elementRef, isVisible];
};

// --- Mockup Components ---
interface StatCardProps {
  label: string;
  value: string;
  trend: string;
  trendUp: boolean;
}

const StatCard = ({ label, value, trend, trendUp }: StatCardProps) => (
  <div className="bg-white/80 p-4 rounded-[24px] border border-white shadow-sm hover:scale-105 transition-transform duration-300">
    <div className="flex justify-between items-start mb-2">
      <span className="text-xs font-bold text-[#9FA0B5] uppercase tracking-wider">{label}</span>
      <div className={`flex items-center text-xs font-bold ${trendUp ? 'text-green-500' : 'text-red-500'}`}>
        {trend} {trendUp ? <TrendingUp className="w-3 h-3 ml-1" /> : <ArrowDownRight className="w-3 h-3 ml-1" />}
      </div>
    </div>
    <div className="text-2xl font-bold text-[#0F172A]">{value}</div>
  </div>
);

// =============================================================================
// HERO SECTION
// =============================================================================
const HeroSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const [placeholderText, setPlaceholderText] = useState("");
  const [currentBrandIndex, setCurrentBrandIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const brands = ["Instamart", "Zepto", "Blinkit"];
  
  // Brand cycling animation
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentBrandIndex((prev) => (prev + 1) % brands.length);
        setIsAnimating(false);
      }, 500); // Half of the transition for fade out, then switch
    }, 2500); // Change brand every 2.5 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    const texts = [
      "Track competitor prices in real-time...",
      "Monitor stock availability across platforms...",
      "Analyze market trends and insights...",
      "Win the Buy Box with intelligence...",
      "Automate pricing and visibility..."
    ];
    let loopNum = 0;
    let charIndex = 0;
    let isDeleting = false;
    let typingSpeed = 100;

    const handleType = () => {
      const currentText = texts[loopNum % texts.length];
      
      if (isDeleting) {
        setPlaceholderText(currentText.substring(0, charIndex - 1));
        charIndex--;
        typingSpeed = 50;
      } else {
        setPlaceholderText(currentText.substring(0, charIndex + 1));
        charIndex++;
        typingSpeed = 100;
      }

      if (!isDeleting && charIndex === currentText.length) {
        isDeleting = true;
        typingSpeed = 2000;
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        loopNum++;
        typingSpeed = 500;
      }

      setTimeout(handleType, typingSpeed);
    };

    const timer = setTimeout(handleType, typingSpeed);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative pt-44 pb-24 px-4 overflow-hidden flex flex-col items-center">
      
      {/* Floating Glass Orbs */}
      <div className="glass-orb w-24 h-24 top-40 left-[10%] opacity-60" style={{ animationDelay: '0s' }}></div>
      <div className="glass-orb w-16 h-16 top-32 right-[15%] opacity-40" style={{ animationDelay: '2s' }}></div>
      <div className="glass-orb w-32 h-32 bottom-40 left-[5%] opacity-30" style={{ animationDelay: '4s' }}></div>
      <div className="glass-orb w-20 h-20 top-[60%] right-[8%] opacity-50" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-4xl mx-auto text-center relative z-10" ref={ref}>
        
        {/* Badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full glass-pill mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
           <Sparkles className="w-4 h-4 text-[#2F7EDA] fill-current" />
           <span className="text-xs font-bold text-[#555663] uppercase tracking-wider">Market Intelligence for Quick Commerce Brands</span>
        </div>

        {/* Headline */}
        <h1 className={`text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-[#0F172A] leading-[1.1] mb-6 md:mb-8 tracking-tight transition-all duration-1000 delay-100 text-center ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          AI Copilot for
          <br />
          <span 
            className={`text-transparent bg-clip-text bg-gradient-to-r from-[#2F7EDA] to-[#5a9ef5] inline-block transition-opacity duration-500 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}
          >
            {brands[currentBrandIndex]}
          </span>
        </h1>

        {/* Subhead */}
        <p className={`text-base sm:text-lg md:text-xl text-[#555663] mb-8 md:mb-12 max-w-2xl mx-auto leading-relaxed px-4 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          Track pricing, stock-outs, and rankings at every dark store in real-time.
        </p>

        {/* Action Pill */}
        <div className={`flex flex-col items-center justify-center gap-4 px-4 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
           {/* Desktop */}
           <div className="hidden sm:flex p-1.5 rounded-full glass-pill items-center w-full max-w-2xl shadow-2xl">
              <div className="px-6 py-3 flex-grow flex items-center justify-start min-w-0">
                <span className="text-[#2F7EDA] font-semibold text-base truncate">
                  {placeholderText}
                  <span className="animate-pulse">|</span>
                </span>
              </div>
              <CalButton 
                className="bg-[#2F7EDA] text-white px-8 py-3 rounded-full font-bold hover:bg-[#1a5bb8] transition-colors shadow-lg whitespace-nowrap flex items-center gap-2"
              >
                Schedule a Quick Call <ArrowRight className="w-4 h-4" />
              </CalButton>
           </div>

           {/* Mobile */}
           <div className="sm:hidden w-full max-w-md">
              <CalButton 
                className="w-full bg-[#2F7EDA] text-white px-6 py-4 rounded-full font-bold hover:bg-[#1a5bb8] transition-colors shadow-lg flex items-center justify-center gap-2 text-base"
              >
                Schedule a Quick Call <ArrowRight className="w-4 h-4" />
              </CalButton>
           </div>
        </div>

        {/* Hero Visual */}
        <div className={`mt-12 md:mt-20 relative w-full max-w-5xl mx-auto transition-all duration-1000 delay-500 transform perspective-1000 ${isVisible ? 'opacity-100 translate-y-0 rotate-x-0' : 'opacity-0 translate-y-40 rotate-x-12'}`}>
           <div className="glass-card rounded-[24px] md:rounded-[40px] p-2 md:p-4 border border-white/50 shadow-2xl bg-white/40 backdrop-blur-md">
              <div className="bg-[#FCFDFD] rounded-[20px] md:rounded-[32px] overflow-hidden border border-[#EDEFF3] shadow-inner relative h-[400px] md:h-[550px]">
                 
                  {/* Dashboard Header */}
                  <div className="absolute top-0 left-0 right-0 h-16 bg-white/80 backdrop-blur border-b border-[#EDEFF3] flex items-center justify-between px-4 sm:px-8 z-10">
                     <div className="flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#FF5F57]"></div>
                        <div className="w-3 h-3 rounded-full bg-[#FEBC2E]"></div>
                        <div className="w-3 h-3 rounded-full bg-[#28C840]"></div>
                     </div>
                     <div className="text-xs font-bold text-[#9FA0B5] uppercase tracking-widest flex items-center gap-2">
                        <Activity className="w-3 h-3" /> <span className="hidden sm:inline">Live Price Monitor</span><span className="sm:hidden">Live</span>
                     </div>
                  </div>

                  {/* Dashboard Content Mock */}
                  <div className="pt-20 md:pt-24 px-4 md:px-8 pb-4 md:pb-8 grid grid-cols-12 gap-4 md:gap-6 h-full">
                     {/* Left Col - Filters */}
                     <div className="col-span-3 hidden lg:flex flex-col gap-4">
                       <div className="text-xs font-bold text-[#555663] uppercase tracking-wider mb-2">Tracked Products</div>
                       {[
                         { name: 'Oat Milk 1L', active: true },
                         { name: 'Almond Butter', active: false },
                         { name: 'Protein Bar', active: false },
                         { name: 'Green Tea', active: false }
                       ].map((item, i) => (
                         <div key={i} className={`h-12 w-full rounded-xl flex items-center px-4 text-sm font-semibold cursor-pointer transition-all ${item.active ? 'bg-[#2F7EDA]/10 text-[#2F7EDA] border border-[#2F7EDA]/20' : 'bg-[#EDEFF3] text-[#9FA0B5] hover:bg-white'}`}>
                           {item.name}
                         </div>
                       ))}
                       <div className="mt-auto p-4 bg-gradient-to-br from-[#2F7EDA] to-[#1a5bb8] rounded-2xl text-white">
                          <div className="text-xs font-bold opacity-80 mb-1">Coverage</div>
                          <div className="text-2xl font-bold">98%</div>
                          <div className="text-xs opacity-60">Dark stores live</div>
                       </div>
                    </div>

                     {/* Main Chart Area */}
                     <div className="col-span-12 lg:col-span-9 flex flex-col gap-4 md:gap-6">
                       
                        <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-3 sm:gap-0 mb-2">
                           <div>
                              <h3 className="text-lg sm:text-xl font-bold text-[#0F172A]">Oat Milk (Barista Edition)</h3>
                              <p className="text-xs sm:text-sm text-[#9FA0B5]">SKU: #OM-1002 &bull; 1L &bull; &#8377;245</p>
                           </div>
                           <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center gap-1 w-fit whitespace-nowrap">
                              <Zap className="w-3 h-3 fill-current" /> <span className="hidden xs:inline">Live Updates</span><span className="xs:hidden">Live</span>
                           </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            {/* Blinkit */}
                            <div className="flex items-center justify-between bg-white border border-[#EDEFF3] p-3 sm:p-4 rounded-2xl shadow-sm hover:border-[#F8CB46] transition-colors group">
                               <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform p-1.5 border border-[#EDEFF3] flex-shrink-0">
                                     <img src="/blinkit_logo.png" alt="Blinkit" className="w-full h-full object-contain" />
                                  </div>
                                  <div className="text-left min-w-0">
                                     <div className="font-bold text-sm sm:text-base text-[#0F172A]">Blinkit</div>
                                     <div className="text-xs text-[#9FA0B5] truncate">Indiranagar</div>
                                  </div>
                               </div>
                              <div className="text-right flex-shrink-0">
                                 <div className="text-base sm:text-lg font-bold text-[#0F172A]">&#8377;260</div>
                                 <div className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap">
                                    <ArrowUpRight className="w-3 h-3" /> <span className="hidden sm:inline">Increased</span><span className="sm:hidden">+</span>
                                 </div>
                              </div>
                           </div>

                            {/* Zepto (Alert) */}
                            <div className="flex items-center justify-between bg-red-50/50 border border-red-100 p-3 sm:p-4 rounded-2xl shadow-sm relative overflow-hidden group">
                               <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500"></div>
                               <div className="flex items-center gap-2 sm:gap-4 min-w-0 pl-2 sm:pl-3">
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform p-1.5 border border-[#EDEFF3] flex-shrink-0">
                                     <img src="/zepto_logo.png" alt="Zepto" className="w-full h-full object-contain" />
                                  </div>
                                  <div className="text-left min-w-0">
                                     <div className="font-bold text-sm sm:text-base text-[#0F172A]">Zepto</div>
                                     <div className="text-xs text-[#555663] truncate">Koramangala</div>
                                  </div>
                               </div>
                              <div className="text-right flex-shrink-0">
                                 <div className="text-base sm:text-lg font-bold text-red-600">&#8377;230</div>
                                 <div className="text-xs font-bold text-red-600 bg-white px-2 py-0.5 rounded-full inline-flex items-center gap-1 border border-red-100 shadow-sm whitespace-nowrap">
                                    <ArrowDownRight className="w-3 h-3" /> <span className="hidden sm:inline">Decreased</span><span className="sm:hidden">-</span>
                                 </div>
                              </div>
                           </div>

                            {/* Instamart */}
                            <div className="flex items-center justify-between bg-white border border-[#EDEFF3] p-3 sm:p-4 rounded-2xl shadow-sm hover:border-[#E8630A] transition-colors group">
                               <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform p-1.5 border border-[#EDEFF3] flex-shrink-0">
                                     <img src="/swiggy_instamart_logo.png" alt="Swiggy Instamart" className="w-full h-full object-contain" />
                                  </div>
                                  <div className="text-left min-w-0">
                                     <div className="font-bold text-sm sm:text-base text-[#0F172A]">Instamart</div>
                                     <div className="text-xs text-[#9FA0B5] truncate">HSR Layout</div>
                                  </div>
                               </div>
                              <div className="text-right flex-shrink-0">
                                 <div className="text-base sm:text-lg font-bold text-[#0F172A]">&#8377;245</div>
                                 <div className="text-xs font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap">Matched</div>
                              </div>
                           </div>
                       </div>

                    </div>
                 </div>

              </div>
           </div>
           
           {/* Floating Elements */}
           <div className="absolute -left-12 top-1/3 glass-pill p-4 rounded-[24px] flex items-center gap-3 animate-float shadow-xl hidden md:flex">
              <div className="w-10 h-10 rounded-full bg-[#FF5F57] flex items-center justify-center text-white">
                 <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="text-left">
                 <div className="text-[10px] font-bold text-[#9FA0B5] uppercase">Alert</div>
                 <div className="text-sm font-bold text-[#0F172A]">Price War Detected</div>
              </div>
           </div>

           <div className="absolute -right-8 bottom-1/4 glass-pill p-4 rounded-[24px] flex items-center gap-3 animate-float shadow-xl hidden md:flex" style={{ animationDelay: '1.5s' }}>
              <div className="w-10 h-10 rounded-full bg-[#2F7EDA] flex items-center justify-center text-white">
                 <TrendingUp className="w-5 h-5" />
              </div>
              <div className="text-left">
                 <div className="text-[10px] font-bold text-[#9FA0B5] uppercase">Trend</div>
                 <div className="text-sm font-bold text-[#0F172A]">+24% Sales</div>
              </div>
           </div>

        </div>

      </div>
    </section>
  );
};

// =============================================================================
// PAIN POINTS SECTION - VISUAL BENTO GRID
// =============================================================================
const PainPointsSection = () => {
  const [ref, isVisible] = useIntersectionObserver();

  return (
    <section className="py-24 md:py-32 px-4 relative overflow-hidden" ref={ref}>
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 max-w-3xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-pill mb-6">
            <AlertCircle className="w-4 h-4 text-[#2F7EDA]" />
            <span className="text-xs font-bold text-[#555663] uppercase tracking-wider">The Problem</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6">
            The Quick Commerce <span className="text-[#2F7EDA]">Blindspot</span>
          </h2>
          <p className="text-[#555663] text-lg leading-relaxed">
            Brands lose crores every quarter because they can't see what's happening at the dark store level.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Card 1 - Price Wars (with competitor price comparison visual) */}
          <div 
            className={`glass-card rounded-[32px] p-8 relative overflow-hidden transition-all duration-700 group hover:shadow-lg ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="w-10 h-10 rounded-xl bg-[#2F7EDA]/10 flex items-center justify-center mb-4">
                  <AlertCircle className="w-5 h-5 text-[#2F7EDA]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-1">Invisible Price Wars</h3>
                <p className="text-sm text-[#9FA0B5]">Competitors undercut you silently</p>
              </div>
            </div>

            {/* Price comparison visual */}
            <div className="bg-[#F8FAFC] rounded-2xl p-4 border border-[#E5E7EB]">
              <div className="flex items-center justify-between text-xs text-[#9FA0B5] mb-3">
                <span>Your Price</span>
                <span>Competitor</span>
              </div>
              
              {/* Price bars */}
              <div className="space-y-3">
                {[
                  { you: '₹249', comp: '₹199', diff: '-20%' },
                  { you: '₹149', comp: '₹129', diff: '-13%' },
                  { you: '₹399', comp: '₹349', diff: '-12%' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div 
                        className="h-6 bg-[#2F7EDA]/20 rounded-lg flex items-center px-3 pain-bar-you"
                        style={{ animationDelay: `${i * 150}ms` }}
                      >
                        <span className="text-xs font-semibold text-[#2F7EDA]">{item.you}</span>
                      </div>
                    </div>
                    <div className="w-12 text-center">
                      <span className="text-xs font-bold text-[#EF4444] pain-diff" style={{ animationDelay: `${i * 150 + 300}ms` }}>
                        {item.diff}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div 
                        className="h-6 bg-[#10B981]/20 rounded-lg flex items-center justify-end px-3 pain-bar-comp"
                        style={{ animationDelay: `${i * 150 + 100}ms` }}
                      >
                        <span className="text-xs font-semibold text-[#10B981]">{item.comp}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-3 pt-3 border-t border-[#E5E7EB] flex items-center justify-center gap-2">
                <ArrowDownRight className="w-4 h-4 text-[#EF4444]" />
                <span className="text-sm font-semibold text-[#EF4444]">23% revenue impact</span>
              </div>
            </div>
          </div>

          {/* Card 2 - Stock-Out Timeline */}
          <div 
            className={`glass-card rounded-[32px] p-8 relative overflow-hidden transition-all duration-700 group hover:shadow-lg ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="w-10 h-10 rounded-xl bg-[#2F7EDA]/10 flex items-center justify-center mb-4">
                  <Clock className="w-5 h-5 text-[#2F7EDA]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-1">Stock-Out Blindness</h3>
                <p className="text-sm text-[#9FA0B5]">48 hour detection delay</p>
              </div>
            </div>

            {/* Timeline visual */}
            <div className="bg-[#F8FAFC] rounded-2xl p-4 border border-[#E5E7EB]">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-[#E5E7EB]">
                  <div className="absolute inset-0 bg-gradient-to-b from-[#EF4444] to-[#2F7EDA] pain-timeline-fill" />
                </div>
                
                {/* Timeline events */}
                <div className="space-y-4 relative">
                  {[
                    { time: '3:00 AM', event: 'Product goes OOS', icon: AlertTriangle, color: '#EF4444', active: true },
                    { time: '9:00 AM', event: 'Sales drop noticed', icon: TrendingUp, color: '#F59E0B', active: false },
                    { time: '+48 hrs', event: 'Report received', icon: Database, color: '#2F7EDA', active: false },
                  ].map((item, i) => (
                    <div 
                      key={i} 
                      className="flex items-center gap-4 pain-timeline-item"
                      style={{ animationDelay: `${i * 200}ms` }}
                    >
                      <div 
                        className={`w-8 h-8 rounded-full flex items-center justify-center relative z-10 ${
                          item.active ? 'bg-white shadow-md' : 'bg-[#F8FAFC]'
                        }`}
                        style={{ border: `2px solid ${item.color}` }}
                      >
                        <item.icon className="w-4 h-4" style={{ color: item.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-bold" style={{ color: item.color }}>{item.time}</div>
                        <div className="text-sm text-[#555663]">{item.event}</div>
                      </div>
                      {item.active && (
                        <div className="pain-live-dot w-2 h-2 rounded-full bg-[#EF4444]" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Card 3 - Data Chaos */}
          <div 
            className={`glass-card rounded-[32px] p-8 relative overflow-hidden transition-all duration-700 group hover:shadow-lg ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '300ms' }}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="w-10 h-10 rounded-xl bg-[#2F7EDA]/10 flex items-center justify-center mb-4">
                  <Database className="w-5 h-5 text-[#2F7EDA]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-1">Data Fragmentation</h3>
                <p className="text-sm text-[#9FA0B5]">15+ sources, zero clarity</p>
              </div>
            </div>

            {/* Scattered data visual */}
            <div className="bg-[#F8FAFC] rounded-2xl p-4 border border-[#E5E7EB] relative overflow-hidden">
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Excel', img: '/Microsoft_Excel-Logo.wine.svg' },
                  { label: 'Blinkit', img: '/blinkit_logo.png' },
                  { label: 'Zepto', img: '/zepto_logo.png' },
                  { label: 'Docs', img: '/google doc.png' },
                  { label: 'Instamart', img: '/swiggy_instamart_logo.png' },
                  { label: 'WhatsApp', img: '/whatsapp-app-round-icon-popular-messenger-social-media-logo_277909-873.avif' },
                  { label: 'PDF', img: '/pdf logo.png' },
                  { label: 'Mail', icon: '📧' },
                ].map((item, i) => (
                  <div 
                    key={i}
                    className="pain-data-tile aspect-square rounded-xl bg-white border border-[#E5E7EB] flex flex-col items-center justify-center gap-1 text-center p-2 hover:border-[#2F7EDA]/30 transition-colors"
                    style={{ 
                      animationDelay: `${i * 80}ms`
                    }}
                  >
                    {item.img ? (
                      <img src={item.img} alt={item.label} className="w-14 h-14 object-contain" />
                    ) : (
                      <span className="text-3xl">{item.icon}</span>
                    )}
                    <span className="text-[10px] text-[#9FA0B5] font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
              
              {/* Chaos indicator */}
              <div className="mt-3 flex items-center justify-center gap-2">
                <div className="pain-chaos-line h-0.5 w-8 bg-[#EF4444]/30 rounded-full" />
                <span className="text-xs text-[#9FA0B5]">No single source of truth</span>
                <div className="pain-chaos-line h-0.5 w-8 bg-[#EF4444]/30 rounded-full" />
              </div>
            </div>
          </div>

          {/* Card 4 - Zero Visibility Map */}
          <div 
            className={`glass-card rounded-[32px] p-8 relative overflow-hidden transition-all duration-700 group hover:shadow-lg ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '400ms' }}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="w-10 h-10 rounded-xl bg-[#2F7EDA]/10 flex items-center justify-center mb-4">
                  <ShieldAlert className="w-5 h-5 text-[#2F7EDA]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-1">Zero Hyperlocal Visibility</h3>
                <p className="text-sm text-[#9FA0B5]">Same keyword, different ranks</p>
              </div>
            </div>

            {/* Search ranking bars visual */}
            <div className="bg-[#F8FAFC] rounded-2xl p-4 border border-[#E5E7EB]">
              {/* Search bar mockup */}
              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-[#E5E7EB] mb-4">
                <Search className="w-4 h-4 text-[#9CA3AF]" />
                <span className="text-sm text-[#555663] font-medium">"oat milk"</span>
              </div>
              
              {/* Ranking positions as vertical bars */}
              <div className="flex justify-between gap-1">
                {[
                  { loc: 'HSR', rank: 2, platform: '/blinkit_logo.png' },
                  { loc: 'BTM', rank: 8, platform: '/zepto_logo.png' },
                  { loc: 'Kora.', rank: null, platform: '/swiggy_instamart_logo.png' },
                  { loc: 'Indira.', rank: 1, platform: '/blinkit_logo.png' },
                  { loc: 'White.', rank: 12, platform: '/zepto_logo.png' },
                ].map((item, i) => {
                  const barHeight = item.rank ? Math.max(24, 80 - (item.rank * 5)) : 32;
                  const barColor = item.rank === null ? '#E5E7EB' : item.rank <= 3 ? '#10B981' : item.rank <= 6 ? '#F59E0B' : '#EF4444';
                  
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center">
                      {/* Platform icon */}
                      <img src={item.platform} alt="" className="w-6 h-6 object-contain mb-2" />
                      
                      {/* Bar container - fixed height for alignment */}
                      <div className="h-20 flex flex-col justify-end items-center">
                        {/* Rank label on top of bar */}
                        <div 
                          className={`text-[10px] font-bold mb-1 ${item.rank === null ? 'text-[#D1D5DB]' : ''}`} 
                          style={{ color: item.rank !== null ? barColor : undefined }}
                        >
                          {item.rank !== null ? `#${item.rank}` : '?'}
                        </div>
                        
                        {/* Bar */}
                        <div 
                          className={`w-7 rounded-t-md ${item.rank === null ? 'border-2 border-dashed border-[#D1D5DB] bg-gray-50' : ''}`}
                          style={{ 
                            height: `${barHeight}px`,
                            backgroundColor: item.rank !== null ? barColor : undefined
                          }}
                        />
                      </div>
                      
                      {/* Location */}
                      <div className="text-[9px] text-[#9CA3AF] font-medium text-center mt-2">
                        {item.loc}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Legend */}
              <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-[#E5E7EB]">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
                  <span className="text-[10px] text-[#9CA3AF]">Top 3</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#F59E0B]"></div>
                  <span className="text-[10px] text-[#9CA3AF]">4-6</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#EF4444]"></div>
                  <span className="text-[10px] text-[#9CA3AF]">7+</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded border border-dashed border-[#D1D5DB]"></div>
                  <span className="text-[10px] text-[#9CA3AF]">No data</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom tagline */}
        <div className={`mt-12 text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-[#9FA0B5] text-sm">
            These aren't just numbers — they're <span className="text-[#2F7EDA] font-semibold">opportunities slipping away</span> every day.
          </p>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// SOLUTIONS SECTION - Clean Visual Grid
// =============================================================================
const SolutionsSection = () => {
  const [ref, isVisible] = useIntersectionObserver();

  const solutions = [
    {
      icon: BarChart3,
      title: "Price Intelligence",
      subtitle: "Real-time competitor tracking",
      visual: "price" // custom visual indicator
    },
    {
      icon: RefreshCw,
      title: "Stock Monitoring", 
      subtitle: "Instant OOS alerts",
      visual: "stock"
    },
    {
      icon: Search,
      title: "Search Tracking",
      subtitle: "Hyperlocal keyword ranks",
      visual: "search"
    },
    {
      icon: Cpu,
      title: "AI Alerts",
      subtitle: "Anomaly detection",
      visual: "ai"
    },
    {
      icon: Network,
      title: "Unified Dashboard",
      subtitle: "All platforms in one view",
      visual: "unified"
    },
    {
      icon: Rocket,
      title: "Smart Playbooks",
      subtitle: "Auto-optimization",
      visual: "playbook"
    }
  ];

  // Mini visualization components
  const PriceVisual = () => (
    <div className="flex items-end gap-1 h-8">
      {[65, 45, 80, 55, 90, 70].map((h, i) => (
        <div 
          key={i}
          className="solution-bar w-2 bg-[#2F7EDA]/80 rounded-sm"
          style={{ 
            height: `${h}%`,
            animationDelay: `${i * 100}ms`
          }}
        />
      ))}
    </div>
  );

  const StockVisual = () => (
    <div className="flex items-center gap-1">
      {[1, 2, 3].map((_, i) => (
        <div 
          key={i}
          className={`solution-dot w-2.5 h-2.5 rounded-full ${i === 2 ? 'bg-[#2F7EDA]' : 'bg-[#2F7EDA]/30'}`}
          style={{ animationDelay: `${i * 200}ms` }}
        />
      ))}
      <RefreshCw className="w-3 h-3 text-[#2F7EDA] ml-1 solution-spin" />
    </div>
  );

  const SearchVisual = () => (
    <div className="flex items-center gap-2">
      <div className="text-lg font-bold text-[#2F7EDA] solution-rank">#1</div>
      <div className="flex flex-col gap-0.5">
        <div className="w-8 h-1 bg-[#2F7EDA] rounded-full" />
        <div className="w-6 h-1 bg-[#2F7EDA]/50 rounded-full" />
        <div className="w-4 h-1 bg-[#2F7EDA]/30 rounded-full" />
      </div>
    </div>
  );

  const AIVisual = () => (
    <div className="relative">
      <div className="solution-pulse w-6 h-6 rounded-full bg-[#2F7EDA]/20 absolute inset-0" />
      <div className="w-6 h-6 rounded-full bg-[#2F7EDA]/40 flex items-center justify-center relative">
        <div className="w-2 h-2 rounded-full bg-[#2F7EDA]" />
      </div>
    </div>
  );

  const UnifiedVisual = () => (
    <div className="flex -space-x-1">
      {['B', 'Z', 'I'].map((letter, i) => (
        <div 
          key={i}
          className="w-5 h-5 rounded-full bg-[#2F7EDA] flex items-center justify-center text-[8px] font-bold text-white border-2 border-white solution-stack"
          style={{ animationDelay: `${i * 100}ms`, zIndex: 3 - i }}
        >
          {letter}
        </div>
      ))}
    </div>
  );

  const PlaybookVisual = () => (
    <div className="flex items-center gap-1">
      <div className="solution-check w-4 h-4 rounded bg-[#2F7EDA]/20 flex items-center justify-center">
        <CheckCircle2 className="w-3 h-3 text-[#2F7EDA]" />
      </div>
      <ArrowRight className="w-3 h-3 text-[#2F7EDA]/50" />
      <div className="w-4 h-4 rounded bg-[#2F7EDA] flex items-center justify-center">
        <Zap className="w-2.5 h-2.5 text-white" />
      </div>
    </div>
  );

  const visuals: Record<string, () => JSX.Element> = {
    price: PriceVisual,
    stock: StockVisual,
    search: SearchVisual,
    ai: AIVisual,
    unified: UnifiedVisual,
    playbook: PlaybookVisual
  };

  return (
    <section className="py-32 px-4 relative overflow-hidden" ref={ref}>
      {/* Subtle background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-[800px] h-[800px] rounded-full blur-[150px] opacity-[0.05] bg-[#2F7EDA] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 max-w-2xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="glass-pill inline-flex items-center gap-2 px-5 py-2.5 mb-8">
            <Sparkles className="w-4 h-4 text-[#2F7EDA]" />
            <span className="text-xs font-bold text-[#2F7EDA] uppercase tracking-wider">The Solution</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4 leading-tight">
            How ReflowAI <span className="text-[#2F7EDA]">Fixes This</span>
          </h2>
          <p className="text-[#555663] text-lg">
            Six tools. One platform. Complete visibility.
          </p>
        </div>

        {/* 3x2 Grid */}
        <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
          {solutions.map((solution, i) => {
            const IconComponent = solution.icon;
            const VisualComponent = visuals[solution.visual];
            
            return (
              <div
                key={i}
                className="solution-visual-card glass-card p-6 group cursor-default"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {/* Top row: Icon + Visual */}
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#2F7EDA]/10 flex items-center justify-center group-hover:bg-[#2F7EDA]/20 transition-colors duration-300">
                    <IconComponent className="w-5 h-5 text-[#2F7EDA]" />
                  </div>
                  <div className="opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                    <VisualComponent />
                  </div>
                </div>

                {/* Title */}
                <h3 className="font-bold text-[#0F172A] mb-1 group-hover:text-[#2F7EDA] transition-colors duration-300">
                  {solution.title}
                </h3>
                
                {/* Subtitle */}
                <p className="text-sm text-[#9FA0B5]">
                  {solution.subtitle}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// FEATURES SECTION
// =============================================================================
const FeaturesSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const [activeCard, setActiveCard] = useState<number | null>(null);

  const features = [
    { 
      icon: Search, 
      title: "Share of Search", 
      desc: "Track your organic rank across 10,000+ keywords. Know exactly where you appear for 'Healthy Snacks' in South Delhi.",
      color: "#2F7EDA"
    },
    { 
      icon: MapPin, 
      title: "Hyperlocal Pricing", 
      desc: "Pricing varies by dark store. See exactly what price customers in Indiranagar vs Koramangala are paying.",
      color: "#9333EA"
    },
    { 
      icon: Zap, 
      title: "Instant Availability", 
      desc: "Get alerted the second your stock goes zero in any dark store so you can replenish before you lose rank.",
      color: "#28C840"
    },
    { 
      icon: TrendingUp, 
      title: "Performance Analytics", 
      desc: "Deep-dive into your sales velocity, conversion rates, and market share trends across all quick commerce platforms.",
      color: "#F59E0B"
    }
  ];

  // Auto-highlight cards sequentially
  useEffect(() => {
    if (!isVisible) return;
    
    const interval = setInterval(() => {
      setActiveCard(prev => {
        if (prev === null) return 0;
        if (prev >= features.length - 1) return null;
        return prev + 1;
      });
    }, 2000);
    
    return () => clearInterval(interval);
  }, [isVisible, features.length]);

  return (
    <section id="solutions" className="py-28 px-4 relative overflow-hidden" ref={ref}>
      {/* Animated background gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 transition-all duration-1000"
          style={{
            background: activeCard !== null ? features[activeCard].color : '#2F7EDA',
            top: '50%',
            right: '-10%',
            transform: 'translateY(-50%)'
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left - Header & Description */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <span className="inline-flex items-center gap-2 text-[#2F7EDA] font-semibold text-sm uppercase tracking-widest">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2F7EDA] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2F7EDA]"></span>
              </span>
              Core Capabilities
            </span>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mt-4 mb-6 leading-tight">
              Master the<br />
              <span className="relative">
                Digital Shelf
                <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12" fill="none" preserveAspectRatio="none">
                  <path 
                    d="M2 10C50 4 150 4 198 10" 
                    stroke="#2F7EDA" 
                    strokeWidth="4" 
                    strokeLinecap="round"
                    className="features-underline-path"
                  />
                </svg>
              </span>
            </h3>
            <p className="text-[#555663] text-lg leading-relaxed mb-8 max-w-md">
              Stop guessing. Get granular, real-time data from every dark store to optimize your quick commerce strategy.
            </p>
            
            {/* CTA with shimmer effect */}
            <div className="flex items-center gap-4">
              <button className="group relative flex items-center gap-2 px-7 py-4 bg-[#0F172A] text-white font-semibold rounded-full overflow-hidden hover:shadow-xl hover:shadow-[#0F172A]/20 transition-all duration-300">
                <span className="relative z-10">Explore Features</span>
                <ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform" />
                {/* Shimmer effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              </button>
            </div>

            {/* Floating stats */}
            <div className={`flex gap-8 mt-12 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {[
                { value: "10K+", label: "Keywords" },
                { value: "5000+", label: "Dark Stores" },
                { value: "<1min", label: "Alerts" }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-bold text-[#0F172A]">{stat.value}</div>
                  <div className="text-sm text-[#555663]">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Feature Cards Stacked */}
          <div className={`space-y-4 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {features.map((feature, i) => (
              <div 
                key={i}
                className={`group relative flex items-start gap-5 p-6 rounded-2xl bg-white border transition-all duration-500 cursor-pointer ${
                  activeCard === i 
                    ? 'border-transparent shadow-xl scale-[1.02]' 
                    : 'border-[#EDEFF3] hover:border-[#2F7EDA]/30 hover:shadow-lg'
                }`}
                style={{ 
                  transitionDelay: `${i * 100}ms`,
                  boxShadow: activeCard === i ? `0 20px 40px -15px ${feature.color}30` : undefined
                }}
                onMouseEnter={() => setActiveCard(i)}
              >
                {/* Animated left border */}
                <div 
                  className={`absolute left-0 top-4 bottom-4 w-1 rounded-full transition-all duration-500 ${
                    activeCard === i ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{ backgroundColor: feature.color }}
                />

                {/* Icon with pulse animation when active */}
                <div 
                  className={`relative flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-500 ${
                    activeCard === i ? 'scale-110' : ''
                  }`}
                  style={{ 
                    backgroundColor: activeCard === i ? feature.color : `${feature.color}15`
                  }}
                >
                  <feature.icon 
                    className={`w-7 h-7 transition-colors duration-500 ${
                      activeCard === i ? 'text-white' : ''
                    }`}
                    style={{ color: activeCard === i ? 'white' : feature.color }}
                  />
                  {/* Pulse ring */}
                  {activeCard === i && (
                    <span className="absolute inset-0 rounded-xl animate-ping opacity-30" style={{ backgroundColor: feature.color }} />
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0 pl-2">
                  <h4 
                    className={`text-xl font-bold mb-2 transition-colors duration-300`}
                    style={{ color: activeCard === i ? feature.color : '#0F172A' }}
                  >
                    {feature.title}
                  </h4>
                  <p className="text-[#555663] text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </div>

                {/* Arrow with slide animation */}
                <div 
                  className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                    activeCard === i ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}
                  style={{ backgroundColor: activeCard === i ? `${feature.color}15` : 'transparent' }}
                >
                  <ArrowRight className="w-5 h-5" style={{ color: feature.color }} />
                </div>
              </div>
            ))}

            {/* Progress indicator */}
            <div className="flex justify-center gap-2 pt-4">
              {features.map((feature, i) => (
                <button
                  key={i}
                  onClick={() => setActiveCard(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    activeCard === i ? 'w-8' : 'w-1.5 bg-[#EDEFF3] hover:bg-[#C6D1D7]'
                  }`}
                  style={{ backgroundColor: activeCard === i ? feature.color : undefined }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// HOW IT WORKS SECTION
// =============================================================================
const HowItWorksSection = () => {
  const [ref, isVisible] = useIntersectionObserver();

  const steps = [
    {
      step: "01",
      title: "Connect Your Catalog",
      description: "Share your product catalog and we map it across Blinkit, Zepto, and Instamart. Setup takes under 24 hours.",
      icon: Database
    },
    {
      step: "02",
      title: "We Start Crawling",
      description: "Our engine begins monitoring pricing, availability, and search rankings across every dark store in your target cities.",
      icon: RefreshCw
    },
    {
      step: "03",
      title: "Get Actionable Alerts",
      description: "Receive real-time alerts on WhatsApp and email. Price drops, stock-outs, ranking changes — nothing escapes your radar.",
      icon: Zap
    },
    {
      step: "04",
      title: "Win the Sales",
      description: "Use hyperlocal insights to optimize pricing, replenish stock faster, and climb search rankings at every dark store.",
      icon: Rocket
    }
  ];

  return (
    <section className="py-24 px-4 relative" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-16 max-w-3xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-sm font-bold text-[#2F7EDA] uppercase tracking-widest mb-4">How It Works</h2>
          <h3 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">Live in 24 Hours</h3>
          <p className="text-[#555663] text-lg leading-relaxed">No complex integrations. No IT team needed. <br /> Just share your catalog and we handle the rest.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((item, i) => (
            <div 
              key={i} 
              className={`relative glass-card rounded-[32px] p-8 flex flex-col items-start group transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Step number */}
              <span className="text-6xl font-bold text-[#2F7EDA]/10 absolute top-6 right-8 select-none">{item.step}</span>
              
              <div className="w-14 h-14 rounded-2xl bg-[#2F7EDA]/10 border border-[#2F7EDA]/20 flex items-center justify-center mb-6 group-hover:bg-[#2F7EDA] group-hover:border-[#2F7EDA] transition-colors duration-300">
                <item.icon className="w-7 h-7 text-[#2F7EDA] group-hover:text-white transition-colors duration-300" />
              </div>

              <h4 className="text-lg font-bold text-[#0F172A] mb-3">{item.title}</h4>
              <p className="text-[#555663] text-sm leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// MODULE OVERVIEW SECTION
// =============================================================================
const ModuleOverviewSection = () => {
  const navigate = useNavigate();
  const [ref, isVisible] = useIntersectionObserver();
  
  return (
    <section className="py-24 px-4 relative" ref={ref}>
      <div className="max-w-7xl mx-auto">
        {/* Glass Container - matching site design */}
        <div className="bg-[#EDEFF3]/50 backdrop-blur-2xl border border-[#C6D1D7]/30 rounded-[48px] md:rounded-[60px] p-8 md:p-16 relative overflow-hidden shadow-sm">
          
          {/* Ambient Background Glows */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#2F7EDA]/10 rounded-full blur-[100px] -translate-y-1/3 translate-x-1/4 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#9FA0B5]/10 rounded-full blur-[80px] translate-y-1/3 -translate-x-1/4 pointer-events-none"></div>
          
          {/* Section Header */}
          <div className={`text-center mb-16 relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#C6D1D7] mb-6 shadow-sm">
              <Cpu className="w-4 h-4 text-[#2F7EDA]" />
              <span className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Platform Modules</span>
            </div>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 tracking-tight">Everything You Need to Win.</h3>
            <p className="text-[#555663] text-lg max-w-2xl mx-auto leading-relaxed">Four powerful modules working together to give you complete command over your Quick Commerce presence.</p>
          </div>

          {/* Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            
            {/* Card 1: Competitor */}
            <div 
              className={`glass-card bg-white/70 backdrop-blur-md border border-[#EDEFF3] rounded-[32px] overflow-hidden flex flex-col group cursor-pointer hover:border-[#FF5F57]/30 hover:shadow-xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: '150ms' }}
              onClick={() => navigate('/competitor')}
            >
              {/* Abstract Visual Top Half */}
              <div className="h-48 bg-gradient-to-br from-[#FEF2F2] to-[#FECACA]/30 p-6 flex flex-col items-center justify-end border-b border-[#EDEFF3] relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF5F57]/10 blur-[40px] rounded-full pointer-events-none"></div>
                 {/* Mini Chart Mockup */}
                 <div className="flex items-end gap-4 h-24 w-full max-w-[180px] z-10">
                    <div className="w-full bg-[#EDEFF3] rounded-t-xl relative h-[70%] group-hover:h-[50%] transition-all duration-500">
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs text-[#555663] font-bold opacity-0 group-hover:opacity-100 transition-opacity delay-100">&#8377;260</div>
                    </div>
                    <div className="w-full bg-[#FF5F57] rounded-t-xl relative h-[40%] group-hover:h-[80%] transition-all duration-500 shadow-lg">
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs text-white font-bold bg-[#FF5F57] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity delay-100">&#8377;230</div>
                    </div>
                 </div>
              </div>
              
              {/* Content Bottom Half */}
              <div className="p-8 flex-grow flex flex-col bg-white/50">
                <div className="w-12 h-12 rounded-[16px] bg-[#FF5F57]/10 border border-[#FF5F57]/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <Activity className="w-6 h-6 text-[#FF5F57]" />
                </div>
                <h4 className="text-2xl font-bold text-[#0F172A] mb-3">Competitor</h4>
                <p className="text-[#555663] leading-relaxed mb-8 flex-grow">See who's stealing your demand. Track competitor prices, promotions, and availability in real-time across all dark stores.</p>
                <div className="flex items-center gap-2 text-sm font-bold text-[#FF5F57] group-hover:gap-3 transition-all mt-auto">
                  Explore Module <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>

            {/* Card 2: Availability */}
            <div 
              className={`glass-card bg-white/70 backdrop-blur-md border border-[#EDEFF3] rounded-[32px] overflow-hidden flex flex-col group cursor-pointer hover:border-[#28C840]/30 hover:shadow-xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: '300ms' }}
              onClick={() => navigate('/availability')}
            >
              <div className="h-48 bg-gradient-to-br from-[#F0FDF4] to-[#BBF7D0]/30 p-6 flex flex-col items-center justify-center border-b border-[#EDEFF3] relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-32 h-32 bg-[#28C840]/10 blur-[40px] rounded-full pointer-events-none"></div>
                 {/* Mini Grid Mockup */}
                 <div className="grid grid-cols-4 gap-3 w-full max-w-[180px] z-10 transform group-hover:scale-105 transition-transform duration-500">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className={`h-8 rounded-[8px] border ${i === 3 ? 'bg-[#FF5F57]/10 border-[#FF5F57]/30 animate-pulse' : 'bg-[#28C840]/10 border-[#28C840]/20 group-hover:bg-[#28C840]/20 group-hover:border-[#28C840]/40'} transition-colors duration-500 flex items-center justify-center`}>
                        {i === 3 && <AlertTriangle className="w-3 h-3 text-[#FF5F57]" />}
                      </div>
                    ))}
                 </div>
              </div>
              
              <div className="p-8 flex-grow flex flex-col bg-white/50">
                <div className="w-12 h-12 rounded-[16px] bg-[#28C840]/10 border border-[#28C840]/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <Database className="w-6 h-6 text-[#28C840]" />
                </div>
                <h4 className="text-2xl font-bold text-[#0F172A] mb-3">Availability</h4>
                <p className="text-[#555663] leading-relaxed mb-8 flex-grow">Never lose sales to empty shelves. Monitor stock levels across 48+ hyperlocal nodes and get instant OOS alerts.</p>
                <div className="flex items-center gap-2 text-sm font-bold text-[#28C840] group-hover:gap-3 transition-all mt-auto">
                  Explore Module <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>

            {/* Card 3: Visibility */}
            <div 
              className={`glass-card bg-white/70 backdrop-blur-md border border-[#EDEFF3] rounded-[32px] overflow-hidden flex flex-col group cursor-pointer hover:border-[#2F7EDA]/30 hover:shadow-xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: '450ms' }}
              onClick={() => navigate('/visibility')}
            >
              <div className="h-48 bg-gradient-to-br from-[#EFF6FF] to-[#BFDBFE]/30 p-6 flex flex-col items-center justify-center border-b border-[#EDEFF3] relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-32 h-32 bg-[#2F7EDA]/10 blur-[40px] rounded-full pointer-events-none"></div>
                 {/* Mini Search Mockup */}
                 <div className="w-full max-w-[180px] z-10 flex flex-col gap-3">
                    <div className="w-full h-8 rounded-full bg-white border border-[#EDEFF3] flex items-center px-3 gap-2 shadow-sm">
                       <Search className="w-3 h-3 text-[#9FA0B5]" />
                       <div className="h-1.5 w-16 bg-[#EDEFF3] rounded-full"></div>
                    </div>
                    <div className="flex flex-col gap-2 pl-2 mt-1">
                       <div className="flex items-center gap-2 group-hover:translate-x-2 transition-transform duration-300">
                          <span className="text-[10px] font-bold text-[#2F7EDA]">#1</span>
                          <div className="h-2 w-24 bg-[#2F7EDA]/20 rounded-full overflow-hidden"><div className="h-full bg-[#2F7EDA] w-[80%]"></div></div>
                       </div>
                       <div className="flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-300 delay-75">
                          <span className="text-[10px] font-bold text-[#9FA0B5]">#2</span>
                          <div className="h-2 w-20 bg-[#EDEFF3] rounded-full"></div>
                       </div>
                    </div>
                 </div>
              </div>
              
              <div className="p-8 flex-grow flex flex-col bg-white/50">
                <div className="w-12 h-12 rounded-[16px] bg-[#2F7EDA]/10 border border-[#2F7EDA]/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <Search className="w-6 h-6 text-[#2F7EDA]" />
                </div>
                <h4 className="text-2xl font-bold text-[#0F172A] mb-3">Visibility</h4>
                <p className="text-[#555663] leading-relaxed mb-8 flex-grow">Own the digital shelf. Track your share of search, organic rankings, and keyword performance across platforms.</p>
                <div className="flex items-center gap-2 text-sm font-bold text-[#2F7EDA] group-hover:gap-3 transition-all mt-auto">
                  Explore Module <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>

            {/* Card 4: Performance */}
            <div 
              className={`glass-card bg-white/70 backdrop-blur-md border border-[#EDEFF3] rounded-[32px] overflow-hidden flex flex-col group cursor-pointer hover:border-[#FEBC2E]/30 hover:shadow-xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: '600ms' }}
              onClick={() => navigate('/performance')}
            >
              <div className="h-48 bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7]/30 p-6 flex flex-col items-center justify-center border-b border-[#EDEFF3] relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-32 h-32 bg-[#FEBC2E]/10 blur-[40px] rounded-full pointer-events-none"></div>
                 {/* Mini Performance Chart Mockup */}
                 <div className="w-full max-w-[180px] z-10 flex flex-col gap-2">
                    {/* Line chart representation */}
                    <div className="relative h-20 w-full">
                      <svg className="w-full h-full" viewBox="0 0 180 80" fill="none">
                        <path 
                          d="M0 60 L30 50 L60 55 L90 35 L120 40 L150 20 L180 25" 
                          stroke="#FEBC2E" 
                          strokeWidth="3" 
                          strokeLinecap="round"
                          fill="none"
                          className="group-hover:stroke-[#F59E0B] transition-colors"
                        />
                        <path 
                          d="M0 60 L30 50 L60 55 L90 35 L120 40 L150 20 L180 25 L180 80 L0 80 Z" 
                          fill="url(#perfGradient)"
                          className="opacity-30 group-hover:opacity-50 transition-opacity"
                        />
                        <defs>
                          <linearGradient id="perfGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FEBC2E" />
                            <stop offset="100%" stopColor="#FEBC2E" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        {/* Animated dot */}
                        <circle cx="150" cy="20" r="4" fill="#FEBC2E" className="group-hover:animate-pulse" />
                      </svg>
                    </div>
                    {/* Stats row */}
                    <div className="flex justify-between text-[10px] font-bold">
                      <span className="text-[#28C840]">+24%</span>
                      <span className="text-[#555663]">This Month</span>
                    </div>
                 </div>
              </div>
              
              <div className="p-8 flex-grow flex flex-col bg-white/50">
                <div className="w-12 h-12 rounded-[16px] bg-[#FEBC2E]/10 border border-[#FEBC2E]/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <TrendingUp className="w-6 h-6 text-[#FEBC2E]" />
                </div>
                <h4 className="text-2xl font-bold text-[#0F172A] mb-3">Performance</h4>
                <p className="text-[#555663] leading-relaxed mb-8 flex-grow">Measure what matters. Track GMV, conversion rates, and ROI across all your Quick Commerce channels in one place.</p>
                <div className="flex items-center gap-2 text-sm font-bold text-[#FEBC2E] group-hover:gap-3 transition-all mt-auto">
                  Explore Module <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// DEEP DIVE SECTION — PRESERVED EXACTLY AS-IS
// =============================================================================
const DeepDiveSection = () => {
  return (
    <section className="py-24 px-4 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Massive Glass Container */}
        <div className="bg-[#EDEFF3]/50 backdrop-blur-2xl border border-white rounded-[60px] p-8 md:p-16 relative overflow-hidden shadow-2xl">
           
           {/* Background Decorations */}
           <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#9FA0B5]/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3"></div>
           <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#2F7EDA]/10 rounded-full blur-[80px] translate-y-1/3 -translate-x-1/4"></div>

           <div className="grid lg:grid-cols-2 gap-16 relative z-10 items-center">
             
             {/* Text Content */}
             <div>
               <div className="inline-block px-5 py-2 rounded-full bg-white border border-[#C6D1D7] mb-8">
                 <span className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Unified Command Center</span>
               </div>
               
               <h3 className="text-4xl md:text-6xl font-bold text-[#0F172A] mb-8 leading-tight">
                 One Canvas to <br/> Rule Them All.
               </h3>
               
               <p className="text-lg text-[#555663] mb-10 leading-relaxed font-medium">
                 Stop juggling 15 Excel sheets and 5 different vendor portals. ReflowAI ingests data from every Quick Commerce platform into a single, actionable dashboard.
               </p>

               <div className="space-y-6">
                 {[
                    "Real-time Data sync with Blinkit, Zepto, Instamart",
                    "Automated alerts for OOS & Price Drops",
                    "Role-based access for Sales Managers"
                 ].map((item, i) => (
                   <div key={i} className="flex items-center gap-4 group">
                     <div className="w-10 h-10 rounded-full bg-white border border-[#C6D1D7] flex items-center justify-center group-hover:border-[#2F7EDA] transition-colors">
                       <CheckCircle2 className="w-5 h-5 text-[#2F7EDA]" />
                     </div>
                     <span className="text-[#0F172A] font-bold text-lg">{item}</span>
                   </div>
                 ))}
               </div>
             </div>

             {/* 3D Dashboard Mockup Container */}
             <div className="relative">
               <div className="bg-white rounded-[40px] p-6 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] border border-[#C6D1D7] transform rotate-[-2deg] hover:rotate-0 transition-all duration-500">
                  {/* Fake UI Header */}
                  <div className="flex items-center justify-between mb-8 border-b border-[#EDEFF3] pb-4">
                    <div className="flex gap-2">
                       <div className="w-3 h-3 rounded-full bg-[#FF5F57]"></div>
                       <div className="w-3 h-3 rounded-full bg-[#FEBC2E]"></div>
                       <div className="w-3 h-3 rounded-full bg-[#28C840]"></div>
                    </div>
                    <div className="text-xs font-bold text-[#9FA0B5] uppercase tracking-widest">Live Dashboard</div>
                  </div>

                  {/* Fake UI Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <StatCard label="Market Share" value="32.4%" trend="1.2%" trendUp={true} />
                    <StatCard label="OOS SKUs" value="12" trend="4" trendUp={false} />
                  </div>

                  {/* Real Mock UI List */}
                  <div className="space-y-3">
                     {[
                       { icon: Activity, title: 'Price Drop Alert', sub: 'Zepto - Indiranagar', val: '-15%', color: '#FF5F57' },
                       { icon: CheckCircle2, title: 'Stock Refilled', sub: 'Blinkit - Koramangala', val: '100%', color: '#28C840' },
                       { icon: TrendingUp, title: 'New Competitor', sub: 'Swiggy - HSR Layout', val: 'New', color: '#FEBC2E' }
                     ].map((item, i) => (
                       <div key={i} className="flex items-center justify-between p-3 rounded-[16px] hover:bg-[#EDEFF3] transition-colors cursor-pointer group">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-[12px] bg-[#EDEFF3] flex-shrink-0 flex items-center justify-center group-hover:bg-white transition-colors">
                               <item.icon className="w-5 h-5" style={{ color: item.color }} />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-[#0F172A]">{item.title}</div>
                              <div className="text-xs text-[#9FA0B5]">{item.sub}</div>
                            </div>
                          </div>
                          <div className="text-right">
                             <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                                {item.val}
                             </span>
                          </div>
                       </div>
                     ))}
                  </div>
               </div>
             </div>

           </div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// CTA SECTION
// =============================================================================
const CTASection = () => {
  const [ref, isVisible] = useIntersectionObserver();

  return (
    <section className="py-32 px-4 text-center" ref={ref}>
      <div className={`max-w-4xl mx-auto relative transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#2F7EDA]/15 rounded-full blur-[100px] pointer-events-none"></div>
        
        <div className="glass-card rounded-[48px] md:rounded-[60px] p-12 md:p-16 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#2F7EDA] via-[#5a9ef5] to-[#9FA0B5]"></div>
          
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2F7EDA]/10 border border-[#2F7EDA]/20 mb-8">
            <Rocket className="w-4 h-4 text-[#2F7EDA]" />
            <span className="text-xs font-bold text-[#2F7EDA] uppercase tracking-wider">Get Started</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">Ready to transform your<br />quick commerce business?</h2>
          <p className="text-lg text-[#555663] mb-10 max-w-2xl mx-auto leading-relaxed">
            Join leading D2C brands using Reflow AI to win in quick commerce.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <CalButton 
              className="inline-flex items-center justify-center px-8 py-4 rounded-full font-bold transition-all duration-300 active:scale-95 text-sm tracking-wide hover:opacity-90 hover:-translate-y-0.5 gap-2"
              style={{ 
                backgroundColor: '#2F7EDA', 
                color: 'white',
                boxShadow: '0 10px 30px -5px rgba(47, 126, 218, 0.4)' 
              }}
            >
              Schedule a Call <ArrowRight className="w-4 h-4" />
            </CalButton>
            <CalButton 
              className="inline-flex items-center justify-center px-8 py-4 rounded-full font-bold transition-all duration-300 active:scale-95 text-sm tracking-wide hover:opacity-90 hover:-translate-y-0.5"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.5)', 
                color: '#555663', 
                border: '1px solid #C6D1D7',
                backdropFilter: 'blur(4px)'
              }}
            >
              Book a Demo
            </CalButton>
          </div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================
export function LandingPage() {
  const brands = ['FizzPop', 'SnackRight', 'EcoHome', 'PureLife', 'Crunchy', 'SipCo', 'BrewMaster', 'NutriBar'];

  return (
    <>
      <div className="landing-page min-h-screen relative selection:bg-[#2F7EDA] selection:text-white overflow-x-hidden">
        
        {/* Fixed Halo Background */}
        <div className="halo-container">
          <div className="halo-gradient w-[800px] h-[800px] bg-[#2F7EDA] top-[-20%] left-[-10%] opacity-30"></div>
          <div className="halo-gradient w-[600px] h-[600px] bg-[#9FA0B5] bottom-[-10%] right-[-10%] opacity-20"></div>
          <div className="halo-gradient w-[400px] h-[400px] bg-[#C6D1D7] top-[40%] left-[30%] opacity-30"></div>
        </div>

        <LandingNavbar />
        
        <main>
          <HeroSection />

          <PainPointsSection />
          
          <SolutionsSection />

          <FeaturesSection />

          <HowItWorksSection />

          {/* Marquee Section - Trusted by Brands */}
          <section className="py-16 border-y border-[#C6D1D7] bg-white/40 backdrop-blur-sm overflow-hidden">
            <div className="text-center mb-8 px-4">
               <h4 className="text-lg md:text-xl font-bold text-[#0F172A]">
                 Trusted by India's fastest-growing brands to <span className="text-[#2F7EDA]">win the quick commerce game</span>
               </h4>
            </div>
            <div className="marquee-wrapper">
              <div className="marquee-content gap-16 px-8">
                 {/* Duplicated List for Seamless Loop */}
                 {[...brands, ...brands, ...brands].map((brand, i) => (
                   <div key={i} className="flex items-center gap-3 opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all cursor-default">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2F7EDA] to-[#1a5bb8] text-white flex items-center justify-center font-bold text-sm shadow-md flex-shrink-0">
                        {brand[0]}
                      </div>
                      <span className="text-xl font-bold text-[#555663]">{brand}</span>
                   </div>
                 ))}
              </div>
            </div>
          </section>
          
          <ModuleOverviewSection />
          
          <DeepDiveSection />
          
          <CTASection />
        </main>

        <LandingFooter />
      </div>
    </>
  );
}
